import { cloneDeep } from '@apollo/client/utilities';
import { MetadataTableName, UserAccountLayout } from '@bechtel/sr-types';
import { ColDef, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { FC, useEffect, useMemo } from 'react';
import { v5 as uuidv5 } from 'uuid';
import { useAppUser } from '~/components/molecules';
import { ClientGrid } from '~/components/organisms/grid';
import { useTableMetadataQuery } from '~/hooks';
import { EquipmentCable } from '../types';

interface EquipmentCableProps {
  items: EquipmentCable[];
  onGridReady: (api: GridApi, name: string) => void;
  gridApi?: GridApi;
  rowDraggableColumn?: string;
  rowDragManaged?: boolean;
}

export const EquipmentCableList: FC<EquipmentCableProps> = ({
  items,
  onGridReady,
  gridApi,
  rowDraggableColumn,
  rowDragManaged,
}) => {
  const { userAccount } = useAppUser();
  const [getTermViewTableMetaData, { data: termViewMetaData }] = useTableMetadataQuery();

  // Get term view metadata
  useEffect(() => {
    if (!userAccount?.defaultProject?.id) return;
    getTermViewTableMetaData({
      variables: {
        name: MetadataTableName.TERM_VIEW,
        projectId: userAccount?.defaultProject?.id,
      },
    });
  }, [userAccount?.defaultProject?.id, getTermViewTableMetaData]);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.selectAll();
  }, [gridApi]);

  const customTableMetaData = useMemo(() => {
    if (!termViewMetaData) return null;
    const customTableMetadata = cloneDeep({ ...termViewMetaData });

    // form custom layout which is applicable to all the terms view
    const termViewSystemLayout: UserAccountLayout = {
      id: uuidv5('CUSTOM_EQUIPMENT_CABLE_VIEW', userAccount?.defaultProject?.id || ''),
      userAccountId: userAccount?.id || '',
      layoutName: 'custom.equipmentCableView',
      description: 'CUSTOM_EQUIPMENT_CABLE_VIEW',
      projectId: userAccount?.defaultProject?.id || '',
      selectClause: 'id, fromEquipmentId, fromEquipmentTag, tag, isCableOrJumper',
      whereClause: null,
      orderByClause: null,
    };
    customTableMetadata.tableMetadata.systemAccountLayouts = [termViewSystemLayout];

    return customTableMetadata;
  }, [termViewMetaData, userAccount]);

  const defaultColDef: ColDef = useMemo(() => {
    return {
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const onGridInitialize = (api: GridApi) => {
    onGridReady && onGridReady(api, 'equipment_cable');
  };

  if (!items || items.length === 0) return;

  return (
    <ClientGrid
      getRowId={(data) => data?.id}
      name="CUSTOM_METADATA"
      rowSelection="multiple"
      hideLayoutHeader={true}
      canCreateLayout={false}
      rowData={items}
      disableNewRow={true}
      skipPermission={true}
      customDefaultColDef={defaultColDef}
      hideFloatingFilter={true}
      onGridInitialize={onGridInitialize}
      readOnly={true}
      containerHorizontalSpacing="100"
      tableMetaData={customTableMetaData}
      systemLayoutName={'custom.equipmentCableView'}
      rowDraggableColumn={rowDraggableColumn || ''}
      rowDragManaged={rowDragManaged || false}
    ></ClientGrid>
  );
};

export default EquipmentCableList;
